import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useI18n } from '../contexts/I18nContext';
import './Slideshow.css';

// Longest-edge width (px) of backend-generated "@thumb" thumbnails.
export const THUMB_W = 400;

// imgSrcSet builds a responsive srcset offering the small thumbnail and the
// full original as candidates. With an appropriate `sizes`, the browser loads
// the thumbnail for small previews and the original for the full-size lightbox.
// Returns undefined when there is no distinct thumbnail (so no srcset is set).
export function imgSrcSet(original, thumbnail) {
  return thumbnail && thumbnail !== original
    ? `${thumbnail} ${THUMB_W}w, ${original} 2000w`
    : undefined;
}

export default function Slideshow({ slides, maxHeight = 130, className = '', onLightboxOpen, onLightboxClose }) {
  const { t } = useI18n();
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  if (!slides || slides.length === 0) return null;

  const go = (n) => { setIdx((n + slides.length) % slides.length); };
  const current = slides[idx] || slides[0];

  const closeLightbox = () => { setLightbox(false); if (onLightboxClose) onLightboxClose(); };

  useEffect(() => {
    if (!lightbox) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft'  && slides.length > 1) setIdx(i => (i - 1 + slides.length) % slides.length);
      else if (e.key === 'ArrowRight' && slides.length > 1) setIdx(i => (i + 1) % slides.length);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightbox]);

  const hasCaption = !current.isCrop && current.title;
  const hasBar = current.title || current.description || current.link;

  const lightboxJSX = lightbox && ReactDOM.createPortal(
    <div className="slideshow-lightbox" onClick={closeLightbox}>
      <div className="slideshow-lb-header" onClick={e => e.stopPropagation()}>
        {slides.length > 1 && (
          <span className="slideshow-lb-counter">{idx + 1} / {slides.length}</span>
        )}
        <button className="slideshow-lb-close" onClick={closeLightbox}>✕</button>
      </div>
      <div className="slideshow-lb-body" onClick={closeLightbox}>
        <img
          src={current.src}
          srcSet={imgSrcSet(current.src, current.thumb)}
          sizes="90vw"
          alt={current.title || ''}
          className="slideshow-lb-img"
          onClick={e => e.stopPropagation()}
        />
        {slides.length > 1 && (
          <>
            <button className="slideshow-lb-nav slideshow-lb-prev" onClick={e => { e.stopPropagation(); go(idx - 1); }}>‹</button>
            <button className="slideshow-lb-nav slideshow-lb-next" onClick={e => { e.stopPropagation(); go(idx + 1); }}>›</button>
          </>
        )}
        {hasBar && (
          <div className="slideshow-lb-bar" onClick={e => e.stopPropagation()}>
            {hasCaption && <div className="slideshow-lb-bar-title">{current.title}</div>}
            {current.description && (
              <div className="slideshow-lb-bar-desc" dangerouslySetInnerHTML={{ __html: current.description }} />
            )}
            {current.link && (
              <a className="slideshow-lb-bar-link" href={current.link} target="_blank" rel="noopener noreferrer">
                {t('common.open_link')}
              </a>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );

  return (
    <div className={`slideshow ${className}`}>
      <img
        key={current.src}
        src={current.src}
        srcSet={imgSrcSet(current.src, current.thumb)}
        sizes="240px"
        alt={current.title || ''}
        className="slideshow-img"
        style={{ maxHeight }}
        onClick={e => { e.stopPropagation(); setLightbox(true); if (onLightboxOpen) onLightboxOpen(); }}
        onError={e => { e.target.parentNode.style.display = 'none'; }}
      />
      {hasCaption && (
        <div className="slideshow-caption" style={{ bottom: slides.length > 1 ? 22 : 4 }}>
          {current.title}
        </div>
      )}
      {slides.length > 1 && (
        <>
          <button className="slideshow-nav slideshow-prev" onClick={e => { e.stopPropagation(); go(idx - 1); }}>‹</button>
          <button className="slideshow-nav slideshow-next" onClick={e => { e.stopPropagation(); go(idx + 1); }}>›</button>
          <div className="slideshow-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`slideshow-dot${i === idx ? ' on' : ''}`}
                onClick={e => { e.stopPropagation(); go(i); }}
              />
            ))}
          </div>
        </>
      )}
      {lightboxJSX}
    </div>
  );
}

export function annSlides(ann, imageId) {
  const slides = [];
  if (imageId && ann.id) {
    const ts = ann.last_change_at ? Math.floor(new Date(ann.last_change_at + 'Z').getTime() / 1000) : 0;
    const cropSrc = `/public/images/${imageId}/annotations/${ann.id}.png${ts ? `?_=${ts}` : ''}`;
    slides.push({ src: cropSrc, title: ann.title || '', isCrop: true });
  }
  [...(ann.images || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).forEach(img =>
    slides.push({ src: img.url, thumb: img.thumbnail || img.url, title: img.title || '', description: img.description || '', link: img.link || '' })
  );
  return slides;
}
